<template>
  <google-map
    v-if="loaded"
    ref="mapRef"
    :api-promise="apiPromise"
    :center="center"
    :street-view-control="streetViewControl"
    :rotate-control="rotateControl"
    :zoom-control="zoomControl"
    :disable-default-ui="disabledDefaultUi"
    :scale-control="scaleControl"
    :fullscreen-control="fullscreenControl"
    :map-type-control="mapTypeControl"
    :zoom="realZoom"
    map-id="DEMO_MAP_ID"
    map-type-id="roadmap"
    :style="style"
    @tilesloaded="emit('loaded')"
    @center_changed="onCenterChanged"
  >
    <slot />
  </google-map>
</template>

<script setup lang="ts">
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { ref } from 'vue';
import { GoogleMap } from 'vue3-google-map';

const props = withDefaults(defineProps<{
  center: google.maps.LatLngLiteral;
  zoom?: number;
  streetViewControl?: boolean;
  rotateControl?: boolean;
  zoomControl?: boolean;
  disabledDefaultUi?: boolean;
  scaleControl?: boolean;
  fullscreenControl?: boolean;
  mapTypeControl?: boolean;
  style?: string;
}>(), {
  zoom: () => 15,
  streetViewControl: () => false,
  rotateControl: () => false,
  zoomControl: () => true,
  disabledDefaultUi: () => false,
  scaleControl: () => false,
  fullscreenControl: () => true,
  mapTypeControl: () => false,
  style: () => 'width: 100%; height: 400px',
});
const emit = defineEmits(['center:changed', 'loaded']);
// https://developers.google.com/maps/documentation/javascript/libraries
// Migrated to v2 functional API: configure with setOptions() then import needed libraries.
setOptions({
  key: window.MAPS_API_KEY,
  v: 'weekly',
});
const mapRef = ref();
const realZoom = ref(props.zoom);

function onCenterChanged() {
  emit('center:changed', {
    lat: mapRef.value.map.getCenter().lat(),
    lng: mapRef.value.map.getCenter().lng(),
  });
}

const loaded = ref(false);
// Load required libraries (maps implicitly via importLibrary('maps'), plus places, routes, marker)
const apiPromise = (async () => {
  try {
    await Promise.all([
      importLibrary('maps'),
      importLibrary('places'),
      importLibrary('routes'),
      importLibrary('marker'),
    ]);
    loaded.value = true;
    // Return the global namespace similar to previous loader.load() usage for compatibility with vue3-google-map
    return google;
  }
  catch (e) {
    console.error('Failed to load Google Maps libraries', e);
    throw e;
  }
})();

defineExpose({
  mapRef,
});
</script>
